import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

export default function StudioChatModeration() {
  const { studioId, gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [studio, setStudio] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [channels, setChannels] = useState([]);
  const [flaggedMessages, setFlaggedMessages] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [moderationRules, setModerationRules] = useState([]);
  const [stats, setStats] = useState({
    totalMessages: 0,
    flaggedMessages: 0,
    activeBans: 0,
    averageToxicity: 0
  });
  const [activeTab, setActiveTab] = useState('overview'); // overview, flagged, bans, rules, channels

  useEffect(() => {
    if (user && studioId) {
      loadStudioData();
    }
  }, [user, studioId, gameId]);

  const loadStudioData = async () => {
    try {
      setLoading(true);

      // Load studio
      const { data: studioData, error: studioError } = await supabase
        .from('gaming_studios')
        .select('*')
        .eq('id', studioId)
        .eq('owner_id', user.id)
        .single();

      if (studioError) throw studioError;
      setStudio(studioData);

      // Load games for this studio
      const { data: gamesData, error: gamesError } = await supabase
        .from('games')
        .select('id, title, cover_image_url')
        .eq('studio_id', studioId)
        .eq('status', 'published');

      if (gamesError) throw gamesError;
      setGames(gamesData || []);

      // Set selected game
      if (gameId) {
        const game = gamesData.find(g => g.id === gameId);
        setSelectedGame(game || gamesData[0]);
      } else {
        setSelectedGame(gamesData[0]);
      }

      if (gamesData && gamesData.length > 0) {
        const targetGameId = gameId || gamesData[0].id;
        await loadChatData(targetGameId);
      }

    } catch (error) {
      console.error('Error loading studio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChatData = async (targetGameId) => {
    try {
      // Load channels for game
      const { data: channelsData } = await supabase
        .from('chat_channels')
        .select('id, name, channel_type, is_active, created_at')
        .eq('game_id', targetGameId)
        .order('created_at', { ascending: false });

      setChannels(channelsData || []);

      const channelIds = channelsData?.map(c => c.id) || [];

      if (channelIds.length === 0) {
        setStats({
          totalMessages: 0,
          flaggedMessages: 0,
          activeBans: 0,
          averageToxicity: 0
        });
        setFlaggedMessages([]);
        setBannedUsers([]);
        return;
      }

      // Load flagged messages
      const { data: flaggedData } = await supabase
        .from('chat_messages')
        .select(`
          id,
          channel_id,
          user_id,
          message,
          toxicity_score,
          flag_reason,
          moderation_action,
          created_at,
          chat_channels!inner (id, name)
        `)
        .in('channel_id', channelIds)
        .eq('is_flagged', true)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(100);

      // Get user profiles for flagged messages
      if (flaggedData && flaggedData.length > 0) {
        const userIds = [...new Set(flaggedData.map(m => m.user_id))];
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, email, full_name')
          .in('id', userIds);

        const profileMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = p;
          return acc;
        }, {});

        const formattedFlagged = flaggedData.map(msg => ({
          ...msg,
          user: profileMap[msg.user_id]
        }));

        setFlaggedMessages(formattedFlagged);
      } else {
        setFlaggedMessages([]);
      }

      // Load banned users for game
      const { data: bansData } = await supabase
        .from('chat_bans')
        .select(`
          id,
          user_id,
          ban_type,
          reason,
          expires_at,
          created_at,
          is_active
        `)
        .eq('game_id', targetGameId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      // Get user profiles for bans
      if (bansData && bansData.length > 0) {
        const userIds = [...new Set(bansData.map(b => b.user_id))];
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, email, full_name')
          .in('id', userIds);

        const profileMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = p;
          return acc;
        }, {});

        const formattedBans = bansData.map(ban => ({
          ...ban,
          user: profileMap[ban.user_id]
        }));

        setBannedUsers(formattedBans);
      } else {
        setBannedUsers([]);
      }

      // Load moderation rules
      const { data: rulesData } = await supabase
        .from('chat_moderation_rules')
        .select('*')
        .eq('game_id', targetGameId)
        .eq('is_active', true);

      setModerationRules(rulesData || []);

      // Calculate stats
      const { count: totalMessages } = await supabase
        .from('chat_messages')
        .select('id', { count: 'exact', head: true })
        .in('channel_id', channelIds)
        .is('deleted_at', null);

      const { count: flaggedCount } = await supabase
        .from('chat_messages')
        .select('id', { count: 'exact', head: true })
        .in('channel_id', channelIds)
        .eq('is_flagged', true)
        .is('deleted_at', null);

      // Calculate average toxicity
      const { data: toxicityData } = await supabase
        .from('chat_messages')
        .select('toxicity_score')
        .in('channel_id', channelIds)
        .not('toxicity_score', 'is', null)
        .is('deleted_at', null);

      const avgToxicity = toxicityData && toxicityData.length > 0
        ? toxicityData.reduce((sum, m) => sum + (parseFloat(m.toxicity_score) || 0), 0) / toxicityData.length
        : 0;

      setStats({
        totalMessages: totalMessages || 0,
        flaggedMessages: flaggedCount || 0,
        activeBans: bansData?.length || 0,
        averageToxicity: avgToxicity
      });

    } catch (error) {
      console.error('Error loading chat data:', error);
    }
  };

  const handleGameChange = (game) => {
    setSelectedGame(game);
    navigate(`/studio/${studioId}/chat-moderation/${game.id}`);
  };

  const handleDeleteMessage = async (messageId) => {
    if (!confirm('Are you sure you want to delete this message?')) return;

    try {
      const { error } = await supabase
        .from('chat_messages')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', messageId);

      if (error) throw error;

      // Reload flagged messages
      await loadChatData(selectedGame.id);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message');
    }
  };

  const handleBanUser = async (userId, duration = null) => {
    const reason = prompt('Ban reason:');
    if (!reason) return;

    try {
      const banData = {
        user_id: userId,
        game_id: selectedGame.id,
        ban_type: duration ? 'temporary' : 'permanent',
        reason: reason,
        banned_by: user.id,
        expires_at: duration ? new Date(Date.now() + duration * 1000).toISOString() : null,
        is_active: true
      };

      const { error } = await supabase
        .from('chat_bans')
        .insert(banData);

      if (error) throw error;

      // Reload data
      await loadChatData(selectedGame.id);
    } catch (error) {
      console.error('Error banning user:', error);
      alert('Failed to ban user');
    }
  };

  const handleUnbanUser = async (banId) => {
    if (!confirm('Are you sure you want to unban this user?')) return;

    try {
      const { error } = await supabase
        .from('chat_bans')
        .update({ is_active: false })
        .eq('id', banId);

      if (error) throw error;

      // Reload data
      await loadChatData(selectedGame.id);
    } catch (error) {
      console.error('Error unbanning user:', error);
      alert('Failed to unban user');
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Messages</p>
              <p className="text-3xl font-bold text-foreground">{stats.totalMessages.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Icon name="MessageSquare" size={24} className="text-blue-500" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Flagged Messages</p>
              <p className="text-3xl font-bold text-foreground">{stats.flaggedMessages.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.totalMessages > 0 ? ((stats.flaggedMessages / stats.totalMessages) * 100).toFixed(2) : 0}% of total
              </p>
            </div>
            <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Flag" size={24} className="text-red-500" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Active Bans</p>
              <p className="text-3xl font-bold text-foreground">{stats.activeBans.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Ban" size={24} className="text-orange-500" />
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Avg Toxicity</p>
              <p className="text-3xl font-bold text-foreground">{(stats.averageToxicity * 100).toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.averageToxicity < 0.3 ? 'Low' : stats.averageToxicity < 0.7 ? 'Medium' : 'High'}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={24} className="text-purple-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Channels List */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Hash" size={20} className="mr-2" />
          Active Channels
        </h3>
        <div className="space-y-3">
          {channels.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No chat channels for this game yet</p>
          ) : (
            channels.map(channel => (
              <div key={channel.id} className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
                <div>
                  <p className="font-medium text-foreground">{channel.name}</p>
                  <p className="text-sm text-muted-foreground capitalize">{channel.channel_type.replace('_', ' ')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${channel.is_active ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'}`}>
                    {channel.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  const renderFlaggedMessages = () => (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Flag" size={20} className="mr-2" />
          Flagged Messages ({flaggedMessages.length})
        </h3>

        {flaggedMessages.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No flagged messages</p>
        ) : (
          <div className="space-y-3">
            {flaggedMessages.map(msg => (
              <div key={msg.id} className="p-4 bg-background rounded-lg border border-border">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">{msg.user?.full_name || msg.user?.email || 'Unknown User'}</p>
                    <p className="text-xs text-muted-foreground">{msg.chat_channels?.name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      msg.toxicity_score > 0.9 ? 'bg-red-500/10 text-red-500' :
                      msg.toxicity_score > 0.7 ? 'bg-orange-500/10 text-orange-500' :
                      'bg-yellow-500/10 text-yellow-500'
                    }`}>
                      {(msg.toxicity_score * 100).toFixed(0)}% toxic
                    </span>
                    <span className="px-2 py-1 text-xs rounded-full bg-gray-500/10 text-gray-500 capitalize">
                      {msg.flag_reason}
                    </span>
                  </div>
                </div>

                <p className="text-foreground mb-3">{msg.message}</p>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDeleteMessage(msg.id)}
                    className="px-3 py-1 text-xs bg-red-500/10 text-red-500 rounded hover:bg-red-500/20 transition-colors"
                  >
                    Delete Message
                  </button>
                  <button
                    onClick={() => handleBanUser(msg.user_id, 86400)}
                    className="px-3 py-1 text-xs bg-orange-500/10 text-orange-500 rounded hover:bg-orange-500/20 transition-colors"
                  >
                    Ban 24hrs
                  </button>
                  <button
                    onClick={() => handleBanUser(msg.user_id)}
                    className="px-3 py-1 text-xs bg-red-500/10 text-red-500 rounded hover:bg-red-500/20 transition-colors"
                  >
                    Permanent Ban
                  </button>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {new Date(msg.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderBans = () => (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Ban" size={20} className="mr-2" />
          Banned Users ({bannedUsers.length})
        </h3>

        {bannedUsers.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No banned users</p>
        ) : (
          <div className="space-y-3">
            {bannedUsers.map(ban => (
              <div key={ban.id} className="p-4 bg-background rounded-lg border border-border">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{ban.user?.full_name || ban.user?.email || 'Unknown User'}</p>
                    <p className="text-xs text-muted-foreground mb-2">{ban.reason}</p>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                        ban.ban_type === 'permanent' ? 'bg-red-500/10 text-red-500' :
                        ban.ban_type === 'shadowban' ? 'bg-purple-500/10 text-purple-500' :
                        'bg-orange-500/10 text-orange-500'
                      }`}>
                        {ban.ban_type}
                      </span>
                      {ban.expires_at && (
                        <span className="text-xs text-muted-foreground">
                          Expires: {new Date(ban.expires_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleUnbanUser(ban.id)}
                    className="px-3 py-1 text-xs bg-green-500/10 text-green-500 rounded hover:bg-green-500/20 transition-colors"
                  >
                    Unban
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Banned on: {new Date(ban.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderRules = () => (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Settings" size={20} className="mr-2" />
          Moderation Rules
        </h3>

        {moderationRules.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No custom moderation rules configured</p>
        ) : (
          <div className="space-y-3">
            {moderationRules.map(rule => (
              <div key={rule.id} className="p-4 bg-background rounded-lg border border-border">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground capitalize">{rule.rule_type.replace('_', ' ')}</p>
                    <pre className="text-xs text-muted-foreground mt-2 bg-muted/30 p-2 rounded">
                      {JSON.stringify(rule.rule_config, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> Moderation rules are configured via the database.
            Contact support to add custom word filters or adjust toxicity thresholds.
          </p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-20 lg:pb-8">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading chat moderation dashboard...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!studio) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-20 lg:pb-8">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center">
              <p className="text-muted-foreground">Studio not found or access denied</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16 pb-20 lg:pb-8">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center text-sm text-muted-foreground mb-4">
              <button onClick={() => navigate(`/studio-dashboard`)} className="hover:text-foreground transition-colors">
                Dashboard
              </button>
              <Icon name="ChevronRight" size={16} className="mx-2" />
              <span className="text-foreground">Chat Moderation</span>
            </div>

            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-3">
                  <Icon name="MessageSquare" size={32} className="text-primary" />
                  Chat Moderation
                </h1>
                <p className="text-muted-foreground">Manage flagged messages, bans, and moderation rules</p>
              </div>
            </div>

            {/* Game Selector */}
            {games.length > 1 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">Select Game</label>
                <select
                  value={selectedGame?.id || ''}
                  onChange={(e) => {
                    const game = games.find(g => g.id === e.target.value);
                    if (game) handleGameChange(game);
                  }}
                  className="w-full md:w-64 px-4 py-2 bg-background border border-border rounded-lg text-foreground"
                >
                  {games.map(game => (
                    <option key={game.id} value={game.id}>{game.title}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-border">
            <div className="flex space-x-8">
              {['overview', 'flagged', 'bans', 'rules'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-4 px-2 font-medium capitalize transition-colors ${
                    activeTab === tab
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'flagged' && renderFlaggedMessages()}
          {activeTab === 'bans' && renderBans()}
          {activeTab === 'rules' && renderRules()}
        </div>
      </main>
    </div>
  );
}
